import { useState } from 'react';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link, useLocation } from 'react-router-dom';
import SiteLogo from './SiteLogo';

const SmallBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    fontSize: '0.6rem',
    height: 15,
    minWidth: 15,
    padding: '0 4px',
    border: `1px solid ${theme.palette.background.paper}`,
    right: 10,
    top: -12,
  },
}));

export function isActiveRoute(pathname, data) {
  if (data.children) {
    return data.children.some((child) => child.route === pathname);
  }
  return pathname === data.route;
}

function NavButton({ data, pathname }) {
  const [hover, setHover] = useState(false);
  const active = isActiveRoute(pathname, data);

  const baseSx = {
    minHeight: 36,
    whiteSpace: 'nowrap',
    color: active ? 'text.primary' : 'text.secondary',
    fontWeight: active ? 700 : 500,
  };

  if (data.children) {
    return (
      <Box
        sx={{ position: 'relative' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <Button component={Link} to={data.route} sx={baseSx}>
          {data.value}
          {data.isNew ? <SmallBadge badgeContent="new" color="error" /> : null}
        </Button>
        <Box
          sx={{
            display: hover ? 'block' : 'none',
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1300,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            minWidth: 130,
            py: 0.5,
            boxShadow: 6,
          }}
        >
          {data.children.map((child) => (
            <Button
              key={child.route}
              component={Link}
              to={child.route}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                px: 2,
                py: 0.75,
                borderRadius: 0,
                color: pathname === child.route ? 'text.primary' : 'text.secondary',
                fontWeight: pathname === child.route ? 700 : 500,
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {child.value}
            </Button>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Button component={Link} to={data.route} sx={baseSx}>
      {data.value}
      {data.isNew ? <SmallBadge badgeContent="new" color="error" /> : null}
    </Button>
  );
}

function SectionNavbar({ mainNav, utilityNav = [] }) {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedRoute, setExpandedRoute] = useState(null);

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <AppBar position="static">
      <Toolbar sx={{ gap: 2 }}>
        <SiteLogo />
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!isMobile &&
            utilityNav.map((d) => (
              <Button
                key={d.route}
                component={Link}
                to={d.route}
                size="small"
                sx={{
                  fontSize: 12,
                  color: pathname === d.route ? 'text.primary' : 'text.secondary',
                  fontWeight: pathname === d.route ? 700 : 500,
                }}
              >
                {d.value}
              </Button>
            ))}
          {isMobile && (
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: 'text.primary' }} aria-label="메뉴 열기">
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Toolbar>

      {!isMobile && (
        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            px: 2,
            py: 0.5,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
          }}
        >
          {mainNav.map((d) => (
            <NavButton key={d.route} data={d} pathname={pathname} />
          ))}
        </Box>
      )}

      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
        <Box sx={{ width: 270 }} role="presentation">
          <Box sx={{ p: 2 }}>
            <SiteLogo />
          </Box>
          <Divider />
          <List>
            {mainNav.map((d) =>
              d.children ? (
                <Box key={d.route}>
                  <ListItemButton
                    onClick={() =>
                      setExpandedRoute((cur) => (cur === d.route ? null : d.route))
                    }
                  >
                    <ListItemText primary={d.value} />
                    {expandedRoute === d.route ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                  <Collapse in={expandedRoute === d.route} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {d.children.map((child) => (
                        <ListItemButton
                          key={child.route}
                          component={Link}
                          to={child.route}
                          selected={pathname === child.route}
                          onClick={closeDrawer}
                          sx={{ pl: 4 }}
                        >
                          <ListItemText primary={child.value} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              ) : (
                <ListItemButton
                  key={d.route}
                  component={Link}
                  to={d.route}
                  selected={pathname === d.route}
                  onClick={closeDrawer}
                >
                  <ListItemText primary={d.value} />
                </ListItemButton>
              )
            )}
          </List>
          {utilityNav.length > 0 && (
            <>
              <Divider />
              <List>
                {utilityNav.map((d) => (
                  <ListItemButton
                    key={d.route}
                    component={Link}
                    to={d.route}
                    selected={pathname === d.route}
                    onClick={closeDrawer}
                  >
                    <ListItemText primary={d.value} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </Box>
      </Drawer>
    </AppBar>
  );
}

export default SectionNavbar;
